import {
  Button,
  Icon,
  Table,
  TablePagination,
  addToast,
} from '@octano/global-ui';
import { CellFormatOptions } from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Badge, UncontrolledTooltip } from 'reactstrap';

import DisplayError from '../../../../components/info/DisplayError';
import DisplayInfo from '../../../../components/info/DisplayInfo';
import Loading from '../../../../components/info/Loading';
import { PathsLayouts } from '../../../../config/routes';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { TableColumns } from '../../../../types/periodTuition';

import {
  Subsidy as SubsidyType,
  getSubsidiesRequest,
  toggleSubsidyActiveRequest,
} from '../api/subsidy';
import { DisableSubsidyModal } from './parts/DisableSubsidyModal';

import styles from '../style.module.scss';

function Subsidy() {
  const [subsidies, setSubsidies] = useState<SubsidyType[]>([]);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showDisableModal, setShowDisableModal] = useState<boolean>(false);
  const [itemToToggle, setItemToToggle] = useState<SubsidyType | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPagination, setShowPagination] = useState<boolean>(false);

  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const history = useHistory();
  const { t } = useTranslation();
  const prefix = 'tariffAndTuition.subsidies';

  const handleToggleActive = async (subsidy: SubsidyType) => {
    const { error } = await toggleSubsidyActiveRequest(subsidy.id);
    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.save'),
      });
    } else {
      addToast({
        icon: 'success',
        color: 'success',
        text: subsidy.isActive
          ? t(`${prefix}.disabledSuccess`)
          : t(`${prefix}.enabledSuccess`),
      });
      getSubsidies(10, currentPage - 1);
    }
    setShowDisableModal(false);
    setItemToToggle(null);
  };

  const columns: TableColumns[] = [
    {
      columnName: 'name',
      headerText: t(`${prefix}.compensationBox`),
      bold: true,
    },
    {
      columnName: 'generalAmount',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      headerText: t(`${prefix}.generalDiscountAmount`),
      cellFormat: (options: CellFormatOptions) => {
        const amount = Number(options.value) || 0;
        const periodAmounts = options.row.periodAmounts || [];
        const hasMultiplePeriods = periodAmounts.length > 1;
        const tooltipId = `subsidy-amounts-${options.row.id}`;

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <span>{`$${Math.round(amount).toLocaleString('es-CL')}`}</span>
            {hasMultiplePeriods && (
              <>
                <span
                  id={tooltipId}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    marginLeft: '8px',
                  }}
                >
                  <Icon name="information" size={16} color="primary" />
                </span>
                <UncontrolledTooltip
                  placement="bottom"
                  target={tooltipId}
                  className={styles.whiteTooltip}
                >
                  <div className="text-start">
                    <strong>{t(`${prefix}.otherDiscounts`)}</strong>
                    {periodAmounts.map((pa: any) => (
                      <div key={pa.periodId}>
                        {pa.period?.name || `Período ${pa.periodId}`}: $
                        {Math.round(pa.amount).toLocaleString('es-CL')}
                      </div>
                    ))}
                  </div>
                </UncontrolledTooltip>
              </>
            )}
          </div>
        );
      },
    },
    {
      columnName: 'isActive',
      headerText: t(`${prefix}.status`),
      width: '130px',
      cellFormat: (options: CellFormatOptions) => (
        <Badge
          color={options.value ? 'primary' : 'danger'}
          style={{ width: 'auto', minWidth: '80px' }}
        >
          {options.value ? t(`${prefix}.enabled`) : t(`${prefix}.disabled`)}
        </Badge>
      ),
    },
    {
      columnName: 'id',
      headerText: t('common.actions.action'),
      cellFormat: (options: CellFormatOptions) => (
        <Button
          text={
            options.row.isActive
              ? t(`${prefix}.disable`)
              : t(`${prefix}.enable`)
          }
          type="button"
          size="sm"
          outlined
          onClick={() => {
            setItemToToggle(options.row);
            if (options.row.isActive) {
              setShowDisableModal(true);
            } else {
              handleToggleActive(options.row);
            }
          }}
        />
      ),
    },
    {
      columnName: 'id',
      headerText: '',
      width: '60px',
      cellFormat: (options: CellFormatOptions) => (
        <span
          onClick={() => {
            history.push(
              `${PathsLayouts.tariffAndTuiton}/subsidies/edit/${options.row.id}`,
            );
          }}
          className="cursor-pointer"
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <Icon name="edit" key="edit" color="primary" />
        </span>
      ),
    },
  ];

  const getSubsidies = useCallback(
    async (items: number = 10, page: number = 0) => {
      const { data, error } = await getSubsidiesRequest(items, page);

      if (error) {
        setErrorLoading(error.code);
        return;
      }

      if (data) {
        setErrorLoading(undefined);
        const { data: subsidyItems = [], total = 0, total_pages = 1 } = data;
        setSubsidies(subsidyItems);
        setTotalItems(total);
        setTotalPages(total_pages);
        setShowPagination(true);
        setCurrentPage(page + 1);
      }

      setLoading(false);
    },
    [setErrorLoading, setLoading],
  );

  useEffect(() => {
    getSubsidies();
  }, [getSubsidies]);

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => setLoading(true)}
      />
    );
  }

  if (loading) {
    return <Loading insideCard className="mt-4" />;
  }

  // Show list view
  return (
    <div className="g-table-container">
      <Button
        text={t(`${prefix}.newSubsidy`)}
        type="button"
        size="sm"
        icon="plus"
        onClick={() =>
          history.push(`${PathsLayouts.tariffAndTuiton}/subsidies/create`)
        }
        className="g-add-button mb-3 mt-3"
      />

      <div className={styles.list}>
        <Table columns={columns} data={subsidies} noResultsText={<div></div>} />
        {subsidies.length === 0 && (
          <DisplayInfo
            insideCard
            title={t(`${prefix}.emptyTitle`)}
            textBody={t(`${prefix}.emptySubtitle`)}
          />
        )}
        {showPagination && totalItems > 10 && (
          <TablePagination
            pagination={{
              currentPage,
              itemsPerPage: 10,
              onChangePage: (page) => getSubsidies(10, page - 1),
              totalItems,
              totalPages,
            }}
          />
        )}
      </div>

      <DisableSubsidyModal
        show={showDisableModal}
        toggleModal={() => {
          setShowDisableModal(false);
          setItemToToggle(null);
        }}
        onConfirm={() => itemToToggle && handleToggleActive(itemToToggle)}
      />
    </div>
  );
}

export default Subsidy;
